/**
 * My Mō Gioielli - Extreme Conversion Luxury Newsletter & Recovery Engine
 * Coupon: CART10 (Auto-applied to Cart, Checkout Session & LocalStorage)
 * Behavior: Exit-intent trigger only; Evolved Footer Discount Recovery Card
 */
(function () {
  'use strict';

  // Anti-Spam: Disposable / Temporary Email Blacklist
  const DISPOSABLE_DOMAINS = [
    'mailinator.com', 'yopmail.com', 'tempmail.com', 'temp-mail.org',
    'guerrillamail.com', 'guerrillamail.net', 'guerrillamail.org',
    'sharklasers.com', '10minutemail.com', '10minutemail.net',
    'trashmail.com', 'trashmail.net', 'dispostable.com',
    'getairmail.com', 'throwawaymail.com', 'mytemp.email',
    'mohmal.com', 'fakeinbox.com', 'burnermail.io', 'dropmail.me',
    'crazymailing.com', 'nada.ltd', 'inboxkitten.com', 'tempail.com',
    'emailondeck.com', 'generator.email', 'maildrop.cc', 'tempmailo.com'
  ];

  // Anti-Spam: Dummy / Placeholder Domains
  const DUMMY_DOMAINS = [
    'example.com', 'example.org', 'example.net', 'test.com', 'test.it',
    'prova.com', 'prova.it', 'fake.com', 'fake.it', 'asdf.com', 'qwer.com',
    'domain.com', 'email.com', 'sito.it', 'sito.com', 'sample.com',
    'dummy.com', 'temp.com', 'invalid.com', 'none.com', 'nessuno.it',
    'ciao.it', 'ciao.com'
  ];

  // Anti-Spam: Common Dummy / Fake Email Blacklist
  const FAKE_EMAILS = [
    'test@test.com', 'test@test.it', 'prova@prova.com', 'prova@prova.it',
    'admin@admin.com', 'asdf@asdf.com', 'user@user.com', 'email@email.com',
    'fake@fake.com', 'a@a.com', 'ciao@ciao.it', 'ciao@ciao.com',
    'nessuno@nessuno.it', 'nome@cognome.it', 'abc@abc.com', 'xyz@xyz.com'
  ];

  const DUMMY_USERS = ['test', 'prova', 'fake', 'asdf', 'qwer', 'dummy', 'admin', 'ciao', 'nessuno', 'noname', '12345'];

  // Primary Valid Providers & TLDs for Levenshtein Typo Detection
  const POPULAR_PROVIDERS = [
    { root: 'gmail', domain: 'gmail.com', validTlds: ['com'] },
    { root: 'hotmail', domain: 'hotmail.com', validTlds: ['com', 'it'] },
    { root: 'outlook', domain: 'outlook.com', validTlds: ['com', 'it'] },
    { root: 'yahoo', domain: 'yahoo.com', validTlds: ['com', 'it'] },
    { root: 'libero', domain: 'libero.it', validTlds: ['it'] },
    { root: 'virgilio', domain: 'virgilio.it', validTlds: ['it'] },
    { root: 'icloud', domain: 'icloud.com', validTlds: ['com'] },
    { root: 'live', domain: 'live.com', validTlds: ['com', 'it'] },
    { root: 'tiscali', domain: 'tiscali.it', validTlds: ['it'] },
    { root: 'fastwebnet', domain: 'fastwebnet.it', validTlds: ['it'] },
    { root: 'alice', domain: 'alice.it', validTlds: ['it'] },
    { root: 'tim', domain: 'tim.it', validTlds: ['it'] }
  ];

  const EXACT_VALID_DOMAINS = new Set([
    'gmail.com', 'hotmail.com', 'hotmail.it', 'outlook.com', 'outlook.it',
    'yahoo.com', 'yahoo.it', 'libero.it', 'virgilio.it', 'icloud.com',
    'live.com', 'live.it', 'tiscali.it', 'fastwebnet.it', 'tim.it',
    'alice.it', 'tin.it', 'me.com', 'msn.com', 'vodafone.it'
  ]);

  function levenshteinDistance(a, b) {
    const m = a.length, n = b.length;
    const d = [];
    for (let i = 0; i <= m; i++) d[i] = [i];
    for (let j = 0; j <= n; j++) d[0][j] = j;
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      }
    }
    return d[m][n];
  }

  function detectDomainTypo(domain) {
    if (!domain || EXACT_VALID_DOMAINS.has(domain)) return null;

    const dotIndex = domain.lastIndexOf('.');
    if (dotIndex === -1) return null;
    const root = domain.slice(0, dotIndex);
    const tld = domain.slice(dotIndex + 1);

    // 1. Check provider root typos (e.g. gnail, gmaill, hotmial, etc.)
    for (let i = 0; i < POPULAR_PROVIDERS.length; i++) {
      const prov = POPULAR_PROVIDERS[i];
      const distRoot = levenshteinDistance(root, prov.root);
      const maxDist = prov.root.length > 5 ? 2 : 1;

      if (distRoot <= maxDist) {
        const targetTld = prov.validTlds.includes(tld) ? tld : prov.validTlds[0];
        const suggested = prov.root + '.' + targetTld;
        if (suggested !== domain) {
          return suggested;
        }
      }
    }

    // 2. Check full domain typo against popular domains (e.g. libero.com -> libero.it)
    for (const validDom of EXACT_VALID_DOMAINS) {
      const dist = levenshteinDistance(domain, validDom);
      const maxD = validDom.length > 8 ? 2 : 1;
      if (dist <= maxD && validDom !== domain) {
        return validDom;
      }
    }

    return null;
  }

  function validateEmail(rawEmail) {
    const email = (rawEmail || '').trim().toLowerCase();

    // 1. Campo vuoto
    if (!email) {
      return { valid: false, message: 'Inserisci il tuo indirizzo email.' };
    }

    // 2. Presenza della chiocciola @
    if (!email.includes('@')) {
      return { valid: false, message: 'Indirizzo incompleto: manca la "@".' };
    }

    // 3. Spam con caratteri ripetuti (es. aaaaaaa@...)
    if (/^(.)\1{4,}@/.test(email)) {
      return { valid: false, message: 'Indirizzo email non valido.' };
    }

    // 4. Verifica singola @
    const parts = email.split('@');
    if (parts.length !== 2) {
      return { valid: false, message: 'L\'indirizzo deve contenere una sola "@".' };
    }

    const userPart = parts[0];
    const domainPart = parts[1];

    // 5. Verifica parte utente (lunghezza minima e caratteri)
    if (!userPart || userPart.length < 2) {
      return { valid: false, message: 'Il nome utente dell\'email è troppo breve.' };
    }
    if (/^[.-]|[.-]$/.test(userPart) || /\.\./.test(userPart)) {
      return { valid: false, message: 'Formato nome utente non valido.' };
    }

    // 6. Verifica struttura del dominio
    if (!domainPart || !domainPart.includes('.')) {
      return { valid: false, message: 'Dominio incompleto (es. @gmail.com o @libero.it).' };
    }

    const domainSubparts = domainPart.split('.');
    const tld = domainSubparts[domainSubparts.length - 1];

    // 7. Verifica estensione TLD (.it, .com, .net, etc.)
    if (!tld || tld.length < 2 || !/^[a-z]+$/.test(tld)) {
      return { valid: false, message: 'Estensione di dominio non valida (es. .it, .com).' };
    }

    // 8. Regex RFC standard
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
    if (!emailRegex.test(email)) {
      return { valid: false, message: 'Formato email non valido.' };
    }

    // 9. Blacklist: email fake e bot noti
    if (FAKE_EMAILS.includes(email)) {
      return { valid: false, message: 'Inserisci un indirizzo email reale.' };
    }

    // 10. Blacklist: domini usa-e-geta / temporanei anti-spam
    if (DISPOSABLE_DOMAINS.includes(domainPart)) {
      return { valid: false, message: 'Le email temporanee non sono ammesse.' };
    }

    // 11. Blacklist: domini dummy / test
    if (DUMMY_DOMAINS.includes(domainPart)) {
      return { valid: false, message: 'Inserisci un dominio email reale.' };
    }

    // 12. User fittizio con domini non verificati
    if (DUMMY_USERS.includes(userPart) && !EXACT_VALID_DOMAINS.has(domainPart)) {
      return { valid: false, message: 'Inserisci un indirizzo email reale.' };
    }

    // 13. Suggerimento typo tramite algoritmo di Levenshtein (es. gnail.com -> gmail.com)
    const typoSuggestion = detectDomainTypo(domainPart);
    if (typoSuggestion) {
      return {
        valid: false,
        message: 'Dominio errato. Forse intendevi @' + typoSuggestion + '?',
        suggestion: typoSuggestion
      };
    }

    return { valid: true, email: email };
  }

  // Auto-apply discount to all stores/cart mechanisms
  function applyDiscountToCart(code) {
    if (!code) code = 'CART10';

    // 1. Shopify Checkout session endpoint
    fetch('/discount/' + encodeURIComponent(code)).catch(function () {});

    // 2. Mini Cart LocalStorage key
    try {
      localStorage.setItem('mymo_applied_discount', code);
    } catch (err) {}

    // 3. Browser cookie for discount
    document.cookie = 'discount_code=' + encodeURIComponent(code) + '; path=/; max-age=' + (60 * 60 * 24 * 30);

    // 4. Update any existing mini-cart discount inputs
    const discountInputs = document.querySelectorAll('#CartDiscountcode, [data-name="discount"]');
    discountInputs.forEach(function (inp) {
      inp.value = code;
    });

    // 5. If theme mini-cart sync function is available, trigger it
    if (typeof window.syncDiscountUI === 'function') {
      try {
        window.syncDiscountUI(code);
      } catch (e) {}
    }
  }

  // Check if customer has active purchase intent (cart/checkout/drawer)
  function hasActivePurchaseIntent() {
    const path = window.location.pathname;
    if (path.includes('/cart') || path.includes('/checkout') || path.includes('/orders/')) {
      return true;
    }
    if (document.body.classList.contains('cart-opened') || 
        document.querySelector('.t4s-mini_cart.is--opened, .is--cart-opened, [data-cart-drawer].is--active')) {
      return true;
    }
    return false;
  }

  // =========================================================================
  // FOOTER NEWSLETTER - EVOLVED DISCOUNT RECOVERY MODULE
  // =========================================================================
  function renderFooterRecoveryCard(couponCode) {
    if (!couponCode) couponCode = 'CART10';

    const footerCol = document.querySelector('.bl-footer-10') || 
                      document.querySelector('.t4s_newsletter_se')?.closest('.t4s-col-inner') ||
                      document.querySelector('.t4s-newsletter-parent')?.closest('.t4s-col-inner');
    if (!footerCol) return;

    const footerContent = footerCol.querySelector('[data-footer-content], .t4s-footer-content') || footerCol;

    const desc = footerContent.querySelector('.t4s-text-bl');
    if (desc) {
      desc.style.display = 'none';
    }

    const newsParent = footerContent.querySelector('.t4s-newsletter-parent');
    if (newsParent) {
      newsParent.style.display = 'none';
    }

    let card = document.getElementById('mymo-footer-coupon-card');
    const overlay = document.getElementById('mymo-popup-overlay');
    const tCopied = (overlay && overlay.dataset.copied) || '✓ COPIATO!';
    const tCopy = (overlay && overlay.dataset.copy) || 'COPIA CODICE';
    const tFooterTitle = (overlay && overlay.dataset.footerTitle) || 'IL TUO PRIVILEGIO CLUB MY MŌ';
    const tFooterSub = (overlay && overlay.dataset.footerSub) || 'Recupera qui il tuo codice di benvenuto del 10% per completare l\'ordine:';
    const tFooterStatus = (overlay && overlay.dataset.footerStatus) || 'Applicato automaticamente al carrello';

    if (!card) {
      card = document.createElement('div');
      card.id = 'mymo-footer-coupon-card';
      card.className = 'mymo-footer-coupon-card';
      card.innerHTML = `
        <div class="mymo-footer-coupon-title">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
            <line x1="7" y1="7" x2="7.01" y2="7"></line>
          </svg>
          <span>${tFooterTitle}</span>
        </div>
        <p class="mymo-footer-coupon-sub">${tFooterSub}</p>
        <div class="mymo-footer-coupon-row">
          <span class="mymo-footer-coupon-code">${couponCode}</span>
          <button type="button" class="mymo-footer-copy-btn" id="mymo-footer-copy-btn">${tCopy}</button>
        </div>
        <div class="mymo-footer-discount-status">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span>${tFooterStatus}</span>
        </div>
      `;
      footerContent.appendChild(card);

      const copyBtn = card.querySelector('#mymo-footer-copy-btn');
      if (copyBtn) {
        copyBtn.addEventListener('click', function () {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(couponCode).then(onCopied).catch(fallbackCopy);
          } else {
            fallbackCopy();
          }

          function onCopied() {
            copyBtn.textContent = tCopied;
            copyBtn.classList.add('is-copied');
            setTimeout(function () {
              copyBtn.textContent = tCopy;
              copyBtn.classList.remove('is-copied');
            }, 3000);
          }

          function fallbackCopy() {
            const tempInput = document.createElement('input');
            tempInput.value = couponCode;
            document.body.appendChild(tempInput);
            tempInput.select();
            document.execCommand('copy');
            document.body.removeChild(tempInput);
            onCopied();
          }
        });
      }
    } else {
      card.style.display = 'flex';
    }
  }

  function initFooterFormInterceptor(couponCode) {
    const footerForm = document.getElementById('t4s-form-footer-12') || document.querySelector('.t4s-footer-content .t4s-newsletter__form');
    if (!footerForm) return;

    const emailInput = footerForm.querySelector('input[type="email"], input[name="contact[email]"], input[name="email"]');
    if (!emailInput) return;

    let footerError = footerForm.querySelector('.mymo-footer-error');
    if (!footerError) {
      footerError = document.createElement('div');
      footerError.className = 'mymo-footer-error';
      footerError.style.cssText = 'display: none; color: #d32f2f; font-size: 11.5px; margin-top: 6px; font-family: var(--mymo-brand-font); align-items: center; gap: 4px;';
      const rowInner = footerForm.querySelector('.t4s-newsletter__inner');
      if (rowInner && rowInner.parentNode) {
        rowInner.parentNode.appendChild(footerError);
      } else {
        footerForm.appendChild(footerError);
      }
    }

    let trustRow = footerForm.querySelector('.mymo-footer-trust-row');
    if (!trustRow) {
      trustRow = document.createElement('div');
      trustRow.className = 'mymo-footer-trust-row';
      trustRow.innerHTML = `
        <span class="mymo-footer-trust-item">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>
          <span>Nessuno spam</span>
        </span>
        <span class="mymo-footer-trust-item">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>
          <span>Sconto 10% immediato</span>
        </span>
      `;
      footerForm.appendChild(trustRow);
    }

    emailInput.addEventListener('input', function () {
      footerError.style.display = 'none';
      emailInput.style.borderColor = '';
    });

    footerForm.addEventListener('submit', function (e) {
      e.preventDefault();
      e.stopPropagation();

      const rawEmail = emailInput.value.trim();
      const validation = validateEmail(rawEmail);

      if (!validation.valid) {
        emailInput.style.borderColor = '#d32f2f';
        footerError.style.display = 'flex';
        footerError.textContent = validation.message;
        emailInput.focus();
        return;
      }

      footerError.style.display = 'none';
      emailInput.style.borderColor = '';

      const validEmail = validation.email;

      // 1. Auto-apply discount
      applyDiscountToCart(couponCode);

      // 2. Mark as converted
      localStorage.setItem('mymo_popup_converted', Date.now().toString());

      // 3. Dispatch to Shopify background newsletter
      const formData = new FormData();
      formData.append('form_type', 'customer');
      formData.append('utf8', '✓');
      formData.append('contact[tags]', 'newsletter');
      formData.append('contact[email]', validEmail);

      fetch('/contact#contact_form', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'text/html' }
      }).catch(function () {});

      // 4. Instantly evolve footer form into recovered coupon card
      renderFooterRecoveryCard(couponCode);
    });
  }

  // =========================================================================
  // MAIN POPUP ENGINE (EXIT-INTENT ONLY)
  // =========================================================================
  function initMymoPopup() {
    const overlay = document.getElementById('mymo-popup-overlay');
    if (!overlay) return;

    const closeBtn = overlay.querySelector('.mymo-popup-close');
    const form = overlay.querySelector('.mymo-popup-form');
    const submitBtn = document.getElementById('mymo-submit-btn');
    const successBox = overlay.querySelector('.mymo-popup-success');
    const formContainer = overlay.querySelector('.mymo-form-container');
    const copyBtn = overlay.querySelector('.mymo-copy-btn');
    const applyBtn = overlay.querySelector('.mymo-apply-btn');
    const emailInput = overlay.querySelector('.mymo-popup-input');
    const errorEl = document.getElementById('mymo-popup-error');
    const errorText = errorEl ? errorEl.querySelector('.mymo-error-text') : null;

    const couponCode = (overlay.dataset.coupon || 'CART10').trim();

    // Dev environment detection (localhost / local dev server / preview mode)
    const isDev = window.location.hostname === '127.0.0.1' || 
                  window.location.hostname === 'localhost' || 
                  window.location.search.includes('preview_theme_id');

    // Shopify Theme Customizer Editor detection
    const isDesignMode = (window.Shopify && window.Shopify.designMode) || 
                         (overlay && overlay.dataset.designMode === 'true') ||
                         window.location.search.includes('editor');

    let isTriggered = false;
    let exitFired = false;

    // Check frequency from localStorage / sessionStorage
    const now = Date.now();
    const convertedTime = parseInt(localStorage.getItem('mymo_popup_converted') || '0', 10);
    const dismissedTime = parseInt(sessionStorage.getItem('mymo_popup_dismissed') || localStorage.getItem('mymo_popup_dismissed') || '0', 10);

    const isConverted = !isDev && convertedTime && (now - convertedTime < 30 * 86400000);
    // In dev mode, NEVER block testing with isDismissed!
    const isDismissed = !isDev && dismissedTime && (now - dismissedTime < 24 * 3600000);

    // Initialize Footer Evolution
    if (convertedTime || localStorage.getItem('mymo_applied_discount') === couponCode) {
      renderFooterRecoveryCard(couponCode);
    } else {
      initFooterFormInterceptor(couponCode);
    }

    function openPopup() {
      if (overlay.classList.contains('is-active')) return;
      overlay.classList.add('is-active');
      document.body.classList.add('mymo-popup-open');
      isTriggered = true;
      if (emailInput) {
        setTimeout(function () {
          emailInput.focus();
        }, 300);
      }
    }

    function closePopup(reason) {
      overlay.classList.remove('is-active');
      document.body.classList.remove('mymo-popup-open');

      if (reason === 'dismiss' && !isConverted && !isDesignMode) {
        sessionStorage.setItem('mymo_popup_dismissed', Date.now().toString());
        localStorage.setItem('mymo_popup_dismissed', Date.now().toString());
      }
      if (isDev) {
        // Reset state so merchant can test multiple times easily
        isTriggered = false;
        exitFired = false;
      }
    }

    // Shopify Theme Customizer Editor handling:
    // Opens ONLY when merchant clicks/selects the Newsletter Popup block in sidebar.
    // Closes immediately when merchant deselects it or selects any other block/section.
    if (isDesignMode) {
      const currentBlockId = overlay.dataset.blockId || '';
      const docRef = document.referrer || '';
      const currentSearch = window.location.search || '';

      // If merchant refreshed while already directly inspecting the popup block
      const isAlreadyFocused = (currentBlockId && (docRef.includes(currentBlockId) || currentSearch.includes(currentBlockId))) ||
                               docRef.includes('section=popups') ||
                               currentSearch.includes('section=popups');

      if (isAlreadyFocused) {
        openPopup();
      }

      function handleBlockSelect(e) {
        const target = e.target;
        const blockId = (e.detail && e.detail.blockId) || '';
        const sectionId = (e.detail && e.detail.sectionId) || '';

        const isOurBlock = (target && (target === overlay || overlay.contains(target) || target.id === 'mymo-popup-overlay')) ||
                           (currentBlockId && blockId && blockId.includes(currentBlockId)) ||
                           (blockId && (blockId.includes('d93d8cf4') || blockId.includes('newsletter'))) ||
                           (sectionId === 'popups');

        if (isOurBlock) {
          openPopup();
        } else {
          // Merchant clicked another section or block: hide popup so view is completely unobstructed
          closePopup();
        }
      }

      function handleBlockDeselect(e) {
        const target = e.target;
        const blockId = (e.detail && e.detail.blockId) || '';

        const isOurBlock = (target && (target === overlay || overlay.contains(target) || target.id === 'mymo-popup-overlay')) ||
                           (currentBlockId && blockId && blockId.includes(currentBlockId)) ||
                           (blockId && (blockId.includes('d93d8cf4') || blockId.includes('newsletter')));

        if (isOurBlock) {
          closePopup();
        }
      }

      document.addEventListener('shopify:block:select', handleBlockSelect);
      document.addEventListener('shopify:block:deselect', handleBlockDeselect);

      document.addEventListener('shopify:section:select', function (e) {
        if (e.detail && e.detail.sectionId === 'popups') {
          openPopup();
        } else {
          closePopup();
        }
      });

      document.addEventListener('shopify:section:deselect', function (e) {
        if (e.detail && e.detail.sectionId === 'popups') {
          closePopup();
        }
      });

      overlay.addEventListener('shopify:block:select', function () {
        openPopup();
      });
      overlay.addEventListener('shopify:block:deselect', function () {
        closePopup();
      });
    }

    function triggerEligiblePopup() {
      if (isTriggered) return;
      if (!isDev) {
        if (isConverted || isDismissed || hasActivePurchaseIntent()) return;
      }
      openPopup();
    }

    // Expose helpers globally for testing in console
    window.mymoOpenPopup = openPopup;
    window.mymoClosePopup = closePopup;
    window.mymoResetTesting = function () {
      localStorage.removeItem('mymo_popup_converted');
      localStorage.removeItem('mymo_popup_dismissed');
      localStorage.removeItem('mymo_applied_discount');
      sessionStorage.clear();
      isTriggered = false;
      exitFired = false;
      console.log('[My Mo] Popup testing state reset. Ready for testing exit intent.');
    };

    // Close Button
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        closePopup('dismiss');
      });
    }

    // Click outside dialog
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) {
        closePopup('dismiss');
      }
    });

    // Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-active')) {
        closePopup('dismiss');
      }
    });

    // Keyboard shortcut for merchants/developers: Shift + P to toggle popup
    document.addEventListener('keydown', function (e) {
      if (e.shiftKey && (e.key === 'P' || e.key === 'p')) {
        if (overlay.classList.contains('is-active')) {
          closePopup('dismiss');
        } else {
          openPopup();
        }
      }
    });

    // =========================================================================
    // REFINED TRUE EXIT-INTENT TRIGGERS (Desktop & Mobile)
    // =========================================================================
    const pageStartTime = Date.now();

    if ((!isConverted || isDev) && !isDesignMode) {
      // 1. Desktop (>= 768px): True Exit-Intent & Gentle Purchase Incentive
      if (window.innerWidth >= 768) {
        // A. True Exit-Intent: Cursor leaving viewport top
        document.documentElement.addEventListener('mouseout', function (e) {
          if (!isDev && (Date.now() - pageStartTime < 2500)) return;
          if (exitFired && !isDev) return;

          // If cursor is still within the document elements, ignore
          if (e.relatedTarget || e.toElement) return;

          if (e.clientY <= 35) {
            exitFired = true;
            triggerEligiblePopup();
          }
        });

        // Mouseleave on document
        document.addEventListener('mouseleave', function (e) {
          if (!isDev && (Date.now() - pageStartTime < 2500)) return;
          if (exitFired && !isDev) return;

          if (e.clientY <= 35) {
            exitFired = true;
            triggerEligiblePopup();
          }
        });

        // B. Desktop Purchase Incentive: After 14s if user browsed and scrolled >= 30%
        let desktopScrolled = false;
        window.addEventListener('scroll', function () {
          const docHeight = document.documentElement.scrollHeight - window.innerHeight;
          if (docHeight > 0 && (window.scrollY / docHeight) >= 0.30) {
            desktopScrolled = true;
          }
        }, { passive: true });

        setTimeout(function () {
          if (exitFired && !isDev) return;
          if (desktopScrolled || window.scrollY > 350) {
            exitFired = true;
            triggerEligiblePopup();
          }
        }, 14000);
      }

      // 2. Mobile (< 768px): Proactive Purchase Incentive (Timer abbassato a 8s + Scroll >= 30%)
      if (window.innerWidth < 768) {
        let maxScrollY = 0;
        let hasEngagedScroll = false;

        window.addEventListener('scroll', function () {
          const currentY = window.scrollY;
          if (currentY > maxScrollY) maxScrollY = currentY;

          const docHeight = document.documentElement.scrollHeight - window.innerHeight;
          const scrollPercent = docHeight > 0 ? (currentY / docHeight) : 0;

          if (currentY > 150 || scrollPercent >= 0.20) {
            hasEngagedScroll = true;
          }

          // A. Scroll Depth Incentive: appena l'utente supera il 30-35% della pagina (dopo 4s)
          if (scrollPercent >= 0.30 && (Date.now() - pageStartTime >= 4000)) {
            if (!exitFired || isDev) {
              exitFired = true;
              triggerEligiblePopup();
            }
          }

          // B. Exit-Intent Flick: ritorno rapido verso l'alto per uscire
          if (maxScrollY > 300 && (maxScrollY - currentY > 180) && currentY <= 120) {
            if (!exitFired || isDev) {
              exitFired = true;
              triggerEligiblePopup();
            }
          }
        }, { passive: true });

        // C. Smart Timer Incentive: 8 secondi (se l'utente ha iniziato a scorrere la pagina)
        const mobileDelaySec = parseInt(overlay.dataset.mobileDelay || '8', 10);
        const mobileDelayMs = Math.max(mobileDelaySec, 8) * 1000;

        setTimeout(function () {
          if (exitFired && !isDev) return;
          if (hasEngagedScroll || window.scrollY > 120 || maxScrollY > 120) {
            exitFired = true;
            triggerEligiblePopup();
          }
        }, mobileDelayMs);
      }
    }

    // Reveal Instant Success & Coupon State in Popup
    function revealSuccessState() {
      localStorage.setItem('mymo_popup_converted', Date.now().toString());
      if (formContainer) {
        formContainer.style.display = 'none';
      }
      if (successBox) {
        successBox.classList.add('is-visible');
      }

      // Auto-apply CART10 to cart & session immediately
      applyDiscountToCart(couponCode);

      // Instantly evolve the footer newsletter into the recovery card
      renderFooterRecoveryCard(couponCode);
    }

    function showEmailError(msg, suggestion) {
      if (emailInput) {
        emailInput.classList.remove('has-error');
        void emailInput.offsetWidth;
        emailInput.classList.add('has-error');
        emailInput.focus();
      }
      if (errorEl) {
        const tErrTypo = (overlay && overlay.dataset.errTypo) || 'Dominio errato. Intendevi @';
        if (suggestion) {
          errorEl.innerHTML = `
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span>${tErrTypo}<button type="button" class="mymo-suggest-btn">@${suggestion}</button>?</span>
          `;
          const btn = errorEl.querySelector('.mymo-suggest-btn');
          if (btn) {
            btn.addEventListener('click', function (e) {
              e.preventDefault();
              e.stopPropagation();
              const cur = emailInput ? emailInput.value.trim() : '';
              const atIdx = cur.indexOf('@');
              if (atIdx !== -1) {
                emailInput.value = cur.slice(0, atIdx) + '@' + suggestion;
              } else {
                emailInput.value = cur + '@' + suggestion;
              }
              clearEmailError();
              emailInput.focus();
            });
          }
        } else {
          errorEl.innerHTML = `
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span class="mymo-error-text">${msg}</span>
          `;
        }
        errorEl.classList.add('is-visible');
        errorEl.style.display = 'flex';
      }
    }

    function clearEmailError() {
      if (emailInput) {
        emailInput.classList.remove('has-error');
        emailInput.style.borderColor = '';
      }
      if (errorEl) {
        errorEl.classList.remove('is-visible');
        errorEl.style.display = 'none';
        errorEl.innerHTML = `
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span class="mymo-error-text"></span>
        `;
      }
    }

    // Async AJAX Form Submission Handler (Instant Gratification + Anti-Spam Validation)
    function handleSubscription() {
      const rawEmail = emailInput ? emailInput.value : '';
      const validation = validateEmail(rawEmail);

      if (!validation.valid) {
        showEmailError(validation.message, validation.suggestion);
        return;
      }

      clearEmailError();
      const validEmail = validation.email;

      // 1. Immediately apply CART10 to cart & session
      applyDiscountToCart(couponCode);

      // 2. Immediately reveal the success state (0ms latency for customer)
      revealSuccessState();

      // 3. Dispatch background registration to Shopify
      const formData = new FormData();
      formData.append('form_type', 'customer');
      formData.append('utf8', '✓');
      formData.append('contact[tags]', 'newsletter');
      formData.append('contact[email]', validEmail);

      fetch('/contact#contact_form', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'text/html' }
      }).catch(function () {});
    }

    // Bind Button Click
    if (submitBtn) {
      submitBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        handleSubscription();
      });
    }

    // Bind Enter Key on Input
    if (emailInput) {
      emailInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          handleSubscription();
        }
      });
      emailInput.addEventListener('input', function () {
        clearEmailError();
      });
    }

    // Prevent any native form submit
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        e.stopPropagation();
        handleSubscription();
        return false;
      });
    }

    // Popup 1-Click Copy Code
    if (copyBtn) {
      const tCopied = (overlay && overlay.dataset.copied) || '✓ COPIATO!';
      const tCopy = (overlay && overlay.dataset.copy) || 'COPIA CODICE';

      copyBtn.addEventListener('click', function () {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(couponCode).then(onCopied).catch(fallbackCopy);
        } else {
          fallbackCopy();
        }

        function onCopied() {
          copyBtn.textContent = tCopied;
          copyBtn.classList.add('is-copied');
          setTimeout(function () {
            copyBtn.textContent = tCopy;
            copyBtn.classList.remove('is-copied');
          }, 3000);
        }

        function fallbackCopy() {
          const tempInput = document.createElement('input');
          tempInput.value = couponCode;
          document.body.appendChild(tempInput);
          tempInput.select();
          document.execCommand('copy');
          document.body.removeChild(tempInput);
          onCopied();
        }
      });
    }

    // Event: Continue Shopping / View Cart Button
    if (applyBtn) {
      applyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        applyDiscountToCart(couponCode);
        closePopup('converted');

        fetch('/cart.js')
          .then(function (res) {
            return res.json();
          })
          .then(function (cart) {
            if (cart.item_count > 0) {
              const cartDrawerBtn = document.querySelector('[data-drawer-options*="cart"], .t4s-site-nav__cart [data-drawer-options]');
              if (cartDrawerBtn) {
                cartDrawerBtn.click();
              }
            }
          })
          .catch(function () {});
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMymoPopup);
  } else {
    initMymoPopup();
  }
})();
